import { API_PREFIX, type ApiResponse, type ErrorBody } from '@stockstop/shared';
import type { z } from 'zod';

/**
 * HTTP client.
 *
 * The single place that knows about the response envelope. Callers receive the
 * payload or an `ApiError`, so no component ever inspects `ok` itself.
 */

export class ApiError extends Error {
  readonly code: ErrorBody['code'];
  readonly issues: ErrorBody['issues'];
  readonly status: number;
  readonly requestId: string | undefined;

  constructor(status: number, body: ErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.issues = body.issues;
    this.requestId = body.requestId;
  }

  /** Message for a specific form field, when the server reported one. */
  issueFor(path: string): string | undefined {
    return this.issues?.find((issue) => issue.path === path)?.message;
  }
}

interface RequestOptions<T> {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /**
   * Prüft die Antwort gegen das Schema, statt sie blind zu übernehmen.
   *
   * Ohne diese Prüfung fällt eine Abweichung zwischen Server und Oberfläche erst
   * beim Zeichnen auf — als `undefined is not an object` mitten in einer
   * Komponente, mit weißer Seite und einem Stapelauszug, der auf React zeigt
   * statt auf die Ursache. Genau so ist ein neues Feld in der Positionsansicht
   * aufgeschlagen, während die API noch in der vorherigen Fassung lief.
   *
   * Mit Prüfung scheitert es dort, wo die Daten ankommen, und mit einem Satz, der
   * sagt, was zu tun ist.
   */
  schema?: z.ZodType<T>;
}

const request = async <T>(path: string, options: RequestOptions<T> = {}): Promise<T> => {
  const { method = 'GET', body, signal, schema } = options;

  let response: Response;
  try {
    response = await fetch(`${API_PREFIX}${path}`, {
      method,
      headers: body === undefined ? {} : { 'content-type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
      ...(signal ? { signal } : {}),
    });
  } catch {
    /* Network-level failure: no envelope exists to unwrap. */
    throw new ApiError(0, {
      code: 'INTERNAL_ERROR',
      message: 'Server nicht erreichbar. Läuft die API?',
    });
  }

  let payload: ApiResponse<T>;
  try {
    payload = (await response.json()) as ApiResponse<T>;
  } catch {
    throw new ApiError(response.status, {
      code: 'INTERNAL_ERROR',
      message: `Unerwartete Antwort (HTTP ${response.status})`,
    });
  }

  if (!payload.ok) {
    throw new ApiError(response.status, payload.error);
  }

  if (!schema) return payload.data;

  const parsed = schema.safeParse(payload.data);
  if (!parsed.success) {
    /*
     * Die häufigste Ursache im Alltag: Die API läuft noch mit dem Code von vor
     * der letzten Schemaänderung. Sie ist ein langlebiger Prozess, die Oberfläche
     * lädt sich dagegen bei jeder Änderung neu — die beiden laufen also
     * regelmäßig auseinander. Deshalb steht der Neustart in der Meldung.
     */
    throw new ApiError(response.status, {
      code: 'INTERNAL_ERROR',
      message:
        'Die Antwort des Servers passt nicht zum erwarteten Format. ' +
        'Läuft die API noch in einer älteren Fassung? Ein Neustart behebt das meist.',
      issues: parsed.error.issues.slice(0, 5).map((issue) => ({
        path: issue.path.join('.'),
        message: issue.message,
      })),
    });
  }

  return parsed.data;
};

export const api = {
  get: <T>(path: string, options: Pick<RequestOptions<T>, 'signal' | 'schema'> = {}) =>
    request<T>(path, options),
  /* `schema` auch beim Schreiben: Eine Antwort, die nicht zum Schema passt, soll
   * hier auffallen und nicht erst dort, wo ein Feld fehlt. */
  post: <T>(path: string, body?: unknown, options: Pick<RequestOptions<T>, 'schema'> = {}) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options: Pick<RequestOptions<T>, 'schema'> = {}) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
